import type { ProcessingActivity } from "@/types/popia";
// Vite ?raw import inlines the CSV content as a string at build time
import ropaCSV from "../../docs/popia/02-Data-Processing-Inventory-ROPA.csv?raw";

/**
 * Parse a single CSV line, handling quoted fields with commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Explicit header -> property map for the ROPA.
 *
 * This is deliberately a map rather than a derivation. The previous code
 * derived the key by uppercasing the letter after each separator, which turned
 * "Processing Activity ID" into `processingActivityID` while the interface
 * declares `processingActivityId`. The compiler could not see the mismatch,
 * because the parser casts a Record<string, string> straight to T, so the
 * symptom was silent: the ID line on every card of /legal/data-processing
 * rendered blank and both pages fell back to array-index React keys.
 *
 * A derivation cannot be made safe here - the same rule has to produce
 * `legalBasisPOPIA` from "Legal Basis (POPIA)" and `processingActivityId` from
 * "Processing Activity ID", and those two demand opposite treatment of a
 * trailing capitalised token. So the map is the contract, and an unmapped
 * header throws at import time rather than yielding a blank field.
 */
const ROPA_KEYS = {
  "Processing Activity ID": "processingActivityId",
  "Activity Name": "activityName",
  Description: "description",
  "Data Categories Collected": "dataCategoriesCollected",
  "Specific Data Fields": "specificDataFields",
  "Data Subjects": "dataSubjects",
  "Purpose of Processing": "purposeOfProcessing",
  "Legal Basis (POPIA)": "legalBasisPOPIA",
  "Source of Data": "sourceOfData",
  "Data Recipients/Processors": "dataRecipientsProcessors",
  "Storage Location": "storageLocation",
  "Retention Period": "retentionPeriod",
  "Security Measures": "securityMeasures",
  "Transborder Transfers": "transborderTransfers",
  "Risk Level": "riskLevel",
  Owner: "owner",
  "Last Reviewed": "lastReviewed",
} as const satisfies Record<string, keyof ProcessingActivity>;

/**
 * CSV parser that handles quoted fields.
 *
 * `keyByHeader` must cover every header in the file. A header with no entry is
 * a programming error and throws, because the alternative - the behaviour this
 * replaced - is a property that is silently absent from every row.
 */
function parseCSV<T>(
  csvContent: string,
  keyByHeader: Record<string, string>,
): T[] {
  const lines = csvContent.split("\n").filter((line) => line.trim());
  if (lines.length === 0) return [];

  const headers = parseCSVLine(lines[0]);
  const unmapped = headers.filter((header) => !keyByHeader[header]);
  if (unmapped.length > 0) {
    throw new Error(
      `POPIA CSV has headers with no property mapping: ${unmapped.join(", ")}`,
    );
  }

  const rows: T[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length === 0) continue;

    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[keyByHeader[header]] = values[index] || "";
    });
    rows.push(row as T);
  }

  return rows;
}

// Parsed once at import time (CSV is inlined by Vite at build time)
const processingActivities: ProcessingActivity[] =
  parseCSV<ProcessingActivity>(ropaCSV, ROPA_KEYS);

export function getProcessingActivities(): ProcessingActivity[] {
  return processingActivities;
}
